import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { bookingsApi } from '@/lib/api/bookings.api';

import type { BookingQuery, CreateBookingDto, UpdateBookingDto } from '@repo/shared-types';

const QUERY_KEY = ['bookings'];

export const useBookings = (query: Partial<BookingQuery> = {}) =>
  useQuery({
    queryKey: [...QUERY_KEY, query],
    queryFn: () => bookingsApi.getAll(query),
  });

export const useBookingsByCall = (callId: string | undefined) =>
  useQuery({
    queryKey: [...QUERY_KEY, 'by-call', callId],
    queryFn: () => bookingsApi.getAll({ callId: callId!, limit: 100 }),
    enabled: !!callId,
  });

export const useBooking = (id: string | undefined) =>
  useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => bookingsApi.getOne(id!),
    enabled: !!id,
  });

export const useCreateBooking = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBookingDto) => bookingsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdateBooking = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingDto }) =>
      bookingsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeleteBooking = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bookingsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useSetAttendance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      attendance,
    }: {
      id: string;
      attendance: 'attended' | 'not_attended' | null;
    }) => bookingsApi.setAttendance(id, attendance),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
